import { useEffect, useState } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  UsersIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Header } from '@/components/Layout';
import { Modal, ConfirmDialog, PageLoader, EmptyState } from '@/components/common';
import { projectsApi, usersApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { PERMISSIONS } from '@/types';
import type { VidaProject, ProjectStatus } from '@/types/product';
import { PROJECT_STATUSES } from '@/types/product';

// ---------------------------------------------------------------------------
// Status badge helper
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: ProjectStatus }) {
  const styles: Record<ProjectStatus, string> = {
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-yellow-100 text-yellow-700',
    archived: 'bg-gray-100 text-gray-600',
  };
  const labels: Record<ProjectStatus, string> = {
    active: 'Activo',
    inactive: 'Inactivo',
    archived: 'Archivado',
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] ?? styles.inactive}`}
    >
      {labels[status] ?? status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ProjectsManager() {
  const { hasPermission } = useAuthStore();

  // List state
  const [projects, setProjects] = useState<VidaProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Modal visibility
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProject, setEditingProject] = useState<VidaProject | null>(null);
  const [deletingProject, setDeletingProject] = useState<VidaProject | null>(null);
  const [managingNodesProject, setManagingNodesProject] = useState<VidaProject | null>(null);
  const [managingUsersProject, setManagingUsersProject] = useState<VidaProject | null>(null);

  // Create / edit form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formStatus, setFormStatus] = useState<ProjectStatus>('active');
  const [saving, setSaving] = useState(false);

  // Delete state
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Manage Nodes state
  const [nodeIds, setNodeIds] = useState<string[]>([]);
  const [newNodeId, setNewNodeId] = useState('');
  const [savingNodes, setSavingNodes] = useState(false);

  // Manage Users state
  const [orgUsers, setOrgUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assigningUserId, setAssigningUserId] = useState<string | null>(null);
  const [unassigningUserId, setUnassigningUserId] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  useEffect(() => {
    loadProjects();
  }, [page, statusFilter]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await projectsApi.list(page, 20, {
        status: statusFilter || undefined,
        search: searchTerm || undefined,
      });
      const data = response.data;
      setProjects(data.projects || []);
      setTotal(data.total || 0);
      setTotalPages(data.pages || 1);
    } catch {
      toast.error('Error al cargar proyectos');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadProjects();
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  // ---------------------------------------------------------------------------
  // Create
  // ---------------------------------------------------------------------------

  const openCreateModal = () => {
    setFormName('');
    setFormDescription('');
    setFormStatus('active');
    setShowCreateModal(true);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSaving(true);
    try {
      await projectsApi.create({
        name: formName.trim(),
        description: formDescription.trim(),
      });
      toast.success('Proyecto creado');
      setShowCreateModal(false);
      setPage(1);
      loadProjects();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Error al crear proyecto';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Edit
  // ---------------------------------------------------------------------------

  const openEditModal = (project: VidaProject) => {
    setEditingProject(project);
    setFormName(project.name);
    setFormDescription(project.description || '');
    setFormStatus(project.status);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProject) return;
    if (!formName.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSaving(true);
    try {
      await projectsApi.update(editingProject._id, {
        name: formName.trim(),
        description: formDescription.trim(),
        status: formStatus,
      });
      toast.success('Proyecto actualizado');
      setEditingProject(null);
      loadProjects();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Error al actualizar proyecto';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------

  const handleDelete = async () => {
    if (!deletingProject) return;
    setDeleteLoading(true);
    try {
      await projectsApi.delete(deletingProject._id);
      toast.success('Proyecto eliminado');
      setDeletingProject(null);
      if (projects.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        loadProjects();
      }
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Error al eliminar proyecto';
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Manage Nodes
  // ---------------------------------------------------------------------------

  const openManageNodesModal = (project: VidaProject) => {
    setManagingNodesProject(project);
    setNodeIds([...(project.allowed_nodes || [])]);
    setNewNodeId('');
  };

  const handleAddNode = () => {
    const trimmed = newNodeId.trim();
    if (!trimmed) return;
    if (nodeIds.includes(trimmed)) {
      toast.error('Ese nodo ya está en la lista');
      return;
    }
    setNodeIds((prev) => [...prev, trimmed]);
    setNewNodeId('');
  };

  const handleRemoveNode = (nodeId: string) => {
    setNodeIds((prev) => prev.filter((n) => n !== nodeId));
  };

  const handleNodeInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddNode();
    }
  };

  const handleSaveNodes = async () => {
    if (!managingNodesProject) return;
    setSavingNodes(true);
    try {
      await projectsApi.update(managingNodesProject._id, { allowed_nodes: nodeIds });
      toast.success('Nodos guardados');
      setManagingNodesProject(null);
      loadProjects();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Error al guardar nodos';
      toast.error(message);
    } finally {
      setSavingNodes(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Manage Users
  // ---------------------------------------------------------------------------

  const openManageUsersModal = async (project: VidaProject) => {
    setManagingUsersProject(project);
    setLoadingUsers(true);
    try {
      const response = await usersApi.list(1, 100);
      const allUsers: any[] = response.data.users || [];
      setOrgUsers(allUsers);
    } catch {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAssignUser = async (userId: string) => {
    if (!managingUsersProject) return;
    setAssigningUserId(userId);
    try {
      await projectsApi.assignUsers(managingUsersProject._id, [userId]);
      toast.success('Usuario asignado');
      setManagingUsersProject((prev) =>
        prev
          ? { ...prev, assigned_users: [...(prev.assigned_users || []), userId] }
          : prev
      );
      loadProjects();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Error al asignar usuario';
      toast.error(message);
    } finally {
      setAssigningUserId(null);
    }
  };

  const handleUnassignUser = async (userId: string) => {
    if (!managingUsersProject) return;
    setUnassigningUserId(userId);
    try {
      await projectsApi.unassignUser(managingUsersProject._id, userId);
      toast.success('Usuario removido');
      setManagingUsersProject((prev) =>
        prev
          ? {
              ...prev,
              assigned_users: (prev.assigned_users || []).filter((id) => id !== userId),
            }
          : prev
      );
      loadProjects();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Error al remover usuario';
      toast.error(message);
    } finally {
      setUnassigningUserId(null);
    }
  };

  const getUserLabel = (user: any): string => {
    const firstName = user.profile?.first_name || '';
    const lastName = user.profile?.last_name || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    return fullName || user.email || user._id;
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading && projects.length === 0) {
    return <PageLoader />;
  }

  const assignedUserIds = new Set(managingUsersProject?.assigned_users || []);
  const assignedUsers = orgUsers.filter((u) => assignedUserIds.has(u._id));
  const availableUsers = orgUsers.filter((u) => !assignedUserIds.has(u._id));

  return (
    <div>
      <Header
        title="Proyectos"
        subtitle="Gestiona los proyectos y sus accesos a nodos y usuarios"
        actions={
          hasPermission(PERMISSIONS.PROJECTS_CREATE) ? (
            <button onClick={openCreateModal} className="btn-primary">
              <PlusIcon className="w-5 h-5 mr-2" />
              Nuevo proyecto
            </button>
          ) : undefined
        }
      />

      <div className="p-6">
        {/* Filter bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar proyectos..."
              className="input pl-9 w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </div>
          <select
            className="input w-full sm:w-48"
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
          >
            <option value="">Todos los estados</option>
            {PROJECT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s === 'active' ? 'Activo' : s === 'inactive' ? 'Inactivo' : 'Archivado'}
              </option>
            ))}
          </select>
          <button onClick={handleSearch} className="btn-secondary whitespace-nowrap">
            Buscar
          </button>
        </div>

        {/* Table */}
        {projects.length === 0 && !loading ? (
          <EmptyState
            icon={FolderIcon}
            title="No hay proyectos"
            description="Crea el primer proyecto para organizar el acceso a nodos"
            action={
              hasPermission(PERMISSIONS.PROJECTS_CREATE)
                ? { label: 'Nuevo proyecto', onClick: openCreateModal }
                : undefined
            }
          />
        ) : (
          <div className="card">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Estado</th>
                    <th>Nodos</th>
                    <th>Usuarios</th>
                    <th>Creado</th>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {projects.map((project) => (
                    <tr key={project._id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-vida-100 rounded-lg">
                            <FolderIcon className="w-4 h-4 text-vida-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{project.name}</p>
                            {project.description && (
                              <p className="text-xs text-gray-400 truncate max-w-xs">
                                {project.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <StatusBadge status={project.status} />
                      </td>
                      <td>
                        <button
                          onClick={() => openManageNodesModal(project)}
                          className="flex items-center gap-1 text-sm text-gray-600 hover:text-vida-600"
                          title="Gestionar nodos"
                        >
                          <CircleStackIcon className="w-4 h-4" />
                          <span>{(project.allowed_nodes || []).length}</span>
                        </button>
                      </td>
                      <td>
                        <button
                          onClick={() => openManageUsersModal(project)}
                          className="flex items-center gap-1 text-sm text-gray-600 hover:text-vida-600"
                          title="Gestionar usuarios"
                        >
                          <UsersIcon className="w-4 h-4" />
                          <span>{(project.assigned_users || []).length}</span>
                        </button>
                      </td>
                      <td className="text-gray-500 text-sm">
                        {new Date(project.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          {hasPermission(PERMISSIONS.PROJECTS_UPDATE) && (
                            <button
                              onClick={() => openEditModal(project)}
                              className="p-1 text-gray-400 hover:text-vida-600"
                              title="Editar"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                          )}
                          {hasPermission(PERMISSIONS.PROJECTS_DELETE) && (
                            <button
                              onClick={() => setDeletingProject(project)}
                              className="p-1 text-gray-400 hover:text-red-600"
                              title="Eliminar"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Página {page} de {totalPages} — {total} proyectos
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                    Anterior
                  </button>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    Siguiente
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Create modal                                                         */}
      {/* ------------------------------------------------------------------ */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Nuevo proyecto"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="label">Nombre *</label>
            <input
              type="text"
              className="input"
              placeholder="Mi proyecto"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div>
            <label className="label">Descripcion</label>
            <textarea
              className="input resize-none"
              rows={3}
              placeholder="Descripcion del proyecto"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowCreateModal(false)}
              disabled={saving}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Creando...' : 'Crear proyecto'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ------------------------------------------------------------------ */}
      {/* Edit modal                                                           */}
      {/* ------------------------------------------------------------------ */}
      <Modal
        open={!!editingProject}
        onClose={() => setEditingProject(null)}
        title="Editar proyecto"
      >
        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label className="label">Nombre *</label>
            <input
              type="text"
              className="input"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div>
            <label className="label">Descripcion</label>
            <textarea
              className="input resize-none"
              rows={3}
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Estado</label>
            <select
              className="input"
              value={formStatus}
              onChange={(e) => setFormStatus(e.target.value as ProjectStatus)}
            >
              {PROJECT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s === 'active' ? 'Activo' : s === 'inactive' ? 'Inactivo' : 'Archivado'}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setEditingProject(null)}
              disabled={saving}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ------------------------------------------------------------------ */}
      {/* Manage Nodes modal                                                   */}
      {/* ------------------------------------------------------------------ */}
      <Modal
        open={!!managingNodesProject}
        onClose={() => setManagingNodesProject(null)}
        title={`Nodos permitidos — ${managingNodesProject?.name ?? ''}`}
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Agrega los IDs de los nodos (ObjectId) que tendra acceso este proyecto. Deja la lista
            vacia para permitir todos los nodos.
          </p>

          {/* Add node input */}
          <div className="flex gap-2">
            <input
              type="text"
              className="input flex-1 font-mono text-sm"
              placeholder="ObjectId del nodo..."
              value={newNodeId}
              onChange={(e) => setNewNodeId(e.target.value)}
              onKeyDown={handleNodeInputKeyDown}
            />
            <button
              type="button"
              onClick={handleAddNode}
              disabled={!newNodeId.trim()}
              className="btn-primary"
            >
              <PlusIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Node list */}
          {nodeIds.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-400 border border-dashed border-gray-300 rounded-lg">
              Sin nodos restringidos — acceso a todos los nodos
            </div>
          ) : (
            <ul className="space-y-2 max-h-60 overflow-y-auto">
              {nodeIds.map((nodeId) => (
                <li
                  key={nodeId}
                  className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg"
                >
                  <span className="font-mono text-sm text-gray-700 truncate">{nodeId}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveNode(nodeId)}
                    className="ml-3 p-1 text-gray-400 hover:text-red-600 flex-shrink-0"
                    title="Quitar nodo"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setManagingNodesProject(null)}
              disabled={savingNodes}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSaveNodes}
              disabled={savingNodes}
            >
              {savingNodes ? 'Guardando...' : 'Guardar nodos'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ------------------------------------------------------------------ */}
      {/* Manage Users modal                                                   */}
      {/* ------------------------------------------------------------------ */}
      <Modal
        open={!!managingUsersProject}
        onClose={() => setManagingUsersProject(null)}
        title={`Usuarios del proyecto — ${managingUsersProject?.name ?? ''}`}
        size="lg"
      >
        {loadingUsers ? (
          <div className="py-10 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-vida-600" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Assigned users */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Usuarios asignados ({assignedUsers.length})
              </h4>
              {assignedUsers.length === 0 ? (
                <p className="text-sm text-gray-400 italic">Sin usuarios asignados</p>
              ) : (
                <ul className="space-y-2 max-h-48 overflow-y-auto">
                  {assignedUsers.map((user) => (
                    <li
                      key={user._id}
                      className="flex items-center justify-between px-3 py-2 bg-green-50 border border-green-200 rounded-lg"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-7 h-7 bg-vida-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-vida-600 font-medium text-xs">
                            {(user.profile?.first_name || user.email || '?')[0].toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {getUserLabel(user)}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        </div>
                      </div>
                      {hasPermission(PERMISSIONS.PROJECTS_ASSIGN) && (
                        <button
                          type="button"
                          onClick={() => handleUnassignUser(user._id)}
                          disabled={unassigningUserId === user._id}
                          className="ml-3 p-1 text-gray-400 hover:text-red-600 flex-shrink-0"
                          title="Quitar del proyecto"
                        >
                          {unassigningUserId === user._id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500" />
                          ) : (
                            <XMarkIcon className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Available users to assign */}
            {hasPermission(PERMISSIONS.PROJECTS_ASSIGN) && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Usuarios disponibles ({availableUsers.length})
                </h4>
                {availableUsers.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">
                    Todos los usuarios ya estan asignados
                  </p>
                ) : (
                  <ul className="space-y-2 max-h-48 overflow-y-auto">
                    {availableUsers.map((user) => (
                      <li
                        key={user._id}
                        className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-gray-500 font-medium text-xs">
                              {(user.profile?.first_name || user.email || '?')[0].toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {getUserLabel(user)}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAssignUser(user._id)}
                          disabled={assigningUserId === user._id}
                          className="ml-3 btn-secondary text-xs px-2 py-1 flex-shrink-0"
                          title="Asignar al proyecto"
                        >
                          {assigningUserId === user._id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-vida-600" />
                          ) : (
                            <PlusIcon className="w-4 h-4" />
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setManagingUsersProject(null)}
              >
                Cerrar
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ------------------------------------------------------------------ */}
      {/* Delete confirmation                                                  */}
      {/* ------------------------------------------------------------------ */}
      <ConfirmDialog
        open={!!deletingProject}
        onClose={() => setDeletingProject(null)}
        onConfirm={handleDelete}
        title="Eliminar proyecto"
        message={`Esta accion es irreversible. El proyecto "${deletingProject?.name}" y su configuracion de nodos y usuarios se eliminaran permanentemente.`}
        confirmText="Eliminar"
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
}
